import { USAGE_ENDPOINT } from '../client/usage-recorder';
import { INSPECTOR_POPOVER_SOURCE } from '../client/inspector-popover';

/** Ids the inline script fills in on StaticInspectorMount's panel. */
export const STATIC_PANEL_CLIPBOARD_ID = 'i18n-inspect-clipboard';
export const STATIC_PANEL_ENGLISH_ID = 'i18n-inspect-english';
export const ENGLISH_MESSAGES_ENDPOINT = '/api/i18n-review/english-messages';

/**
 * Vanilla-JS inline script for routes that skip client hydration (homepage, careers
 * pages, see StaticInspectorMount.tsx). Mirrors two hydrated-route behaviors that
 * would otherwise need React:
 *   - usage recording: `<Msg>`'s recordUsage() call lives inside a `useEffect`, which
 *     never runs without hydration, so markers are baked into the SSR HTML but never
 *     reported. This walks the DOM once on load instead.
 *   - the hover popover and its I / T / E keys (inspector-popover.ts, the same code the
 *     hydrated overlay runs). English comes from ENGLISH_MESSAGES_ENDPOINT since the client
 *     catalogue chunks aren't loaded here; "Show English" swaps plain-text strings in the DOM.
 *   - the outline CSS (OVERLAY_STYLE) is gated on a `.i18n-inspect-on` ancestor class,
 *     normally toggled on `<body>` by InspectorOverlay's `useEffect`, which, again,
 *     never runs here. Added to `<html>` instead (a CSS descendant selector matches
 *     any ancestor, not just a direct parent, so this is equivalent).
 *
 * Only ever rendered when the inspector is enabled (StaticInspectorMount returns null
 * otherwise), so this never reaches a normal visitor's page.
 */
export function buildStaticInspectorScript(): string {
  return `(function(){
document.documentElement.classList.add('i18n-inspect-on');
try{
  var route=window.location.pathname;
  var els=document.querySelectorAll('[data-i18n-inspectable]');
  var seen={},entries=[];
  for(var i=0;i<els.length;i++){
    var id=els[i].getAttribute('data-i18n-id');
    if(!id||seen[id])continue;
    seen[id]=true;
    entries.push({id:id,route:route});
  }
  if(entries.length>0){
    fetch('${USAGE_ENDPOINT}',{
      method:'POST',
      credentials:'same-origin',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify({entries:entries}),
    }).catch(function(){});
  }
}catch(e){}
var english=null;
var clip=document.getElementById('${STATIC_PANEL_CLIPBOARD_ID}');
var enBtn=document.getElementById('${STATIC_PANEL_ENGLISH_ID}');
var lang=(document.documentElement.lang||'en').slice(0,2).toLowerCase();
var popover=(${INSPECTOR_POPOVER_SOURCE})({
  selector:'[data-i18n-inspectable]',
  idLabel:'Id',
  getId:function(el){return el.getAttribute('data-i18n-id')||'';},
  getEnglish:function(el){if(!english)return english===false?null:undefined;var v=english[el.getAttribute('data-i18n-id')||''];return v==null?null:v;},
  locale:lang,
  onCopy:function(t){if(clip){clip.textContent=t;clip.removeAttribute('data-empty');}},
  onEnglishChange:function(on){if(enBtn)enBtn.setAttribute('aria-pressed',on?'true':'false');}
});
if(lang!=='en'){
  fetch('${ENGLISH_MESSAGES_ENDPOINT}',{credentials:'same-origin'})
    .then(function(r){if(!r.ok)throw new Error('HTTP '+r.status);return r.json();})
    .then(function(m){english=m;})
    .catch(function(){english=false;});
}
if(enBtn){
  if(lang==='en')enBtn.hidden=true;
  enBtn.addEventListener('click',function(){popover.toggleEnglish();});
}
})();`;
}
